import { inject, Injectable } from '@angular/core';
import { Display } from 'src/app/api/services/display';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  DISPLAY_ACCESS,
  SCHEDULE_ACCESS,
} from 'src/app/auth/types/access-level';
import { MoveFolderDialogComponent } from 'src/app/components/folders/move-folder-dialog/move-folder-dialog.component';
import { RenameDialogComponent } from 'src/app/components/input-dialog/rename-dialog.component';
import { ModalService } from 'src/app/components/modals/modal.service';
import {
  AccessControlList,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import {
  ListOperation,
  ListOperations,
} from 'src/app/components/scrolling-list';
import type { ListItem } from 'src/app/components/scrolling-list/services/scrolling-list-state.service';
import { CompanyIdModalComponent } from 'src/app/components/scrolling-list/company-id-modal/company-id-modal.component';
import { SchedulePickerModalComponent } from 'src/app/schedules/components/schedule-picker-modal/schedule-picker-modal.component';
import { ScheduleService } from 'src/app/schedules/services/schedule.service';
import { EditAddressComponent } from '../components/edit-address/edit-address.component';
import { EditAlertVolumeComponent } from '../components/edit-alert-volume/edit-alert-volume.component';
import { EditKeyOperationModeComponent } from '../components/edit-key-operation-mode/edit-key-operation-mode.component';
import { EditMonitoringComponent } from '../components/edit-monitoring/edit-monitoring.component';
import { EditRebootTimeComponent } from '../components/edit-reboot-time/edit-reboot-time.component';
import { EditScreenSharingComponent } from '../components/edit-screen-sharing/edit-screen-sharing.component';
import { DISPLAY_FOLDER_OPERATION_SERVICE } from '../providers/display-folder-operation.provider';
import { DisplayControlService } from './display-control.service';
import { DisplayService } from './display.service';
import { PlayerActionsService } from './player-actions.service';
import { PlayerLicenseService } from './player-license.service';
import { PlayerProService } from './player-pro.service';
import { FirmwareUpdateService } from './firmware-update.service';

@Injectable({
  providedIn: 'root',
})
export class DisplayListOperationsService {
  private permissionService = inject(PermissionService);

  listOperations = {
    name: 'Display',
    allowSharedAccess: true,
    operations: [
      ((remoteChildren: ListOperation<Display>[]) =>
        ({
          name: 'Remote Commands',
          submenu: true,
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          children: remoteChildren,
          hidden: (selected) =>
            !selected?.length ||
            !remoteChildren.some((c) => !c.separator && !c.hidden?.(selected)),
        }) as ListOperation<Display>)([
        {
          name: 'Reboot Media Player',
          beforeBatchAction: (selected) => {
            return this.folderOperationService.executeSingleBeforeBatchAction(
              (items) =>
                this._checkLicenses(items).then(() =>
                  this.modalService.confirm(
                    'Reboot media player?',
                    'The media player will reboot and the content showing on your display will be interrupted. Do you wish to proceed?',
                    'Yes',
                    'No',
                  ),
                ),
              selected,
            );
          },
          filter: (listItem) => this._listItemEligibleForReboot(listItem),
          actionCall: (item) => {
            return this.folderOperationService.executeSingleOperation(
              this.playerActionsService.rebootByObject.bind(
                this.playerActionsService,
              ),
              item.item,
            );
          },
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          hidden: (selected) => {
            return !selected || selected.length === 0;
          },
        },
        {
          separator: true,
        } as ListOperation<Display>,
        {
          name: 'Restart Rise Player',
          beforeBatchAction: (selected) => {
            return this.folderOperationService.executeSingleBeforeBatchAction(
              (items) =>
                this._checkLicenses(items).then(() =>
                  this.modalService.confirm(
                    'Restart Rise Player?',
                    'Rise Player will restart and the content showing on your display will be interrupted. Do you wish to proceed?',
                    'Yes',
                    'No',
                  ),
                ),
              selected,
            );
          },
          filter: (listItem) =>
            this.playerProService.isRestartCompatiblePlayer(
              listItem.item as Display,
            ),
          actionCall: (item) => {
            return this.folderOperationService.executeSingleOperation(
              this.playerActionsService.restartByObject.bind(
                this.playerActionsService,
              ),
              item.item,
            );
          },
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          hidden: (selected) => {
            return !selected || selected.length === 0;
          },
        },
        {
          name: 'Launch Rise Player',
          beforeBatchAction: (selected) => {
            return this.folderOperationService.executeSingleBeforeBatchAction(
              (items) =>
                this._checkLicenses(items).then(() =>
                  this.modalService.confirm(
                    'Launch Rise Player?',
                    'Rise Player will be launched on the device. Do you wish to proceed?',
                    'Yes',
                    'No',
                  ),
                ),
              selected,
            );
          },
          filter: (listItem) =>
            this.playerProService.isRestartCompatiblePlayer(
              listItem.item as Display,
            ),
          actionCall: (item) => {
            return this.folderOperationService.executeSingleOperation(
              (display) =>
                this.playerActionsService.sendNotificationByObject(
                  display as Display,
                  PlayerActionsService.ACTION_TYPES.LAUNCH,
                ),
              item.item,
            );
          },
          requireRole: 'sa',
          hidden: (selected) => {
            return !this.userStateService.hasRole('sa') || !selected?.length;
          },
        },
        {
          separator: true,
        } as ListOperation<Display>,
        {
          name: 'Turn On Screen',
          beforeBatchAction: (selected) => {
            return this.folderOperationService.executeSingleBeforeBatchAction(
              (items) =>
                this._checkLicenses(items).then(() =>
                  this.modalService.confirm(
                    'Screen Power On?',
                    'The screen will be powered on. Do you wish to proceed?',
                    'Yes',
                    'No',
                  ),
                ),
              selected,
            );
          },
          filter: (listItem) => this._listItemEligibleForScreenPower(listItem),
          actionCall: (item) => {
            return this.folderOperationService.executeSingleOperation(
              (display) =>
                this.playerActionsService.sendNotificationByObject(
                  display as Display,
                  PlayerActionsService.ACTION_TYPES.POWER_ON,
                ),
              item.item,
            );
          },
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          hidden: (selected) => {
            return !selected || selected.length === 0;
          },
        },
        {
          name: 'Turn Off Screen',
          beforeBatchAction: (selected) => {
            return this.folderOperationService.executeSingleBeforeBatchAction(
              (items) =>
                this._checkLicenses(items).then(() =>
                  this.modalService.confirm(
                    'Screen Power Off?',
                    'The screen will be powered off. Do you wish to proceed?',
                    'Yes',
                    'No',
                  ),
                ),
              selected,
            );
          },
          filter: (listItem) => this._listItemEligibleForScreenPower(listItem),
          actionCall: (item) => {
            return this.folderOperationService.executeSingleOperation(
              (display) =>
                this.playerActionsService.sendNotificationByObject(
                  display as Display,
                  PlayerActionsService.ACTION_TYPES.POWER_OFF,
                ),
              item.item,
            );
          },
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
          hidden: (selected) => {
            return !selected || selected.length === 0;
          },
        },
      ] as ListOperation<Display>[]),
      {
        name: 'Update Firmware',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmUpdateFirmware(items),
            selected,
          );
        },
        actionCall: (item, args) => {
          const firmwareUrl = args?.firmwareUrl;
          return this.folderOperationService.executeSingleOperation(
            (display) =>
              this.playerActionsService.updateFirmwareByObject(
                display,
                firmwareUrl,
              ),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        hidden: (selected) => {
          if (!selected || selected.length === 0) {
            return true;
          }
          // Only show for LG webOS Players - check if all selected items are LG webOS Players
          return !selected.every((item: any) =>
            this.playerProService.isLGWebOSPlayer(item.item),
          );
        },
      },
      {
        name: 'License',
        actionCall: (selected) => {
          return this.folderOperationService
            .executeGroupOperation(
              (selection) =>
                this._licenseDisplays(selection.companyId, selection.items),
              { ...selected, items: selected.items.map((item) => item.item) },
            )
            .catch(() => {
              this._licenseError = true;
            });
        },
        beforeBatchAction: (selected) =>
          this._confirmLicense(selected.map((item) => item.item)),
        afterOperationBatchActions: () => this._showLicenseErrorIfNeeded(),
        groupBy: 'companyId',
        filter: (listItem) => {
          return !(listItem.item as Display).playerProAuthorized;
        },
        deepFilter: true,
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        mainOperation: true,
        reloadList: true,
      },
      {
        name: 'Screen Sharing',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmScreenSharing(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Lock Buttons',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmLockKeyOperation(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        filter: (listItem) => {
          return this.playerProService.isKeyOperationModeDeviceCompatiblePlayer(
            listItem.item as Display,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Assign Schedule',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmAssignSchedule(items),
            selected,
          );
        },
        actionCall: (selected, schedule) => {
          return this.folderOperationService.executeGroupOperation(
            (selection) =>
              this.scheduleService.addAllToDistribution(
                selection.items,
                schedule,
              ),
            { ...selected, items: selected.items as unknown as Display[] },
          );
        },
        groupBy: true,
        requireAccess: SCHEDULE_ACCESS.FULL_ACCESS,
      },
      {
        separator: true,
      },
      {
        name: 'Set Monitoring',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetMonitoring(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Set Reboot Time',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetRebootTime(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Set Address',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetAddress(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Control Alert Volume',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmControlAlertVolume(items),
            selected,
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item,
          );
        },
        filter: (listItem) => {
          return this.playerProService.isVolumeControlCompatiblePlayer(
            listItem.item as Display,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Screen Power On',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) =>
              this._checkLicenses(items).then(() =>
                this.modalService.confirm(
                  'Screen Power On?',
                  'The screen will be powered on. Do you wish to proceed?',
                  'Yes',
                  'No',
                ),
              ),
            selected,
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            (display) =>
              this.playerActionsService.sendNotificationByObject(
                display as Display,
                PlayerActionsService.ACTION_TYPES.POWER_ON,
              ),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        hidden: (selected) => {
          if (!selected || selected.length === 0) {
            return true;
          }
          return !selected.every((item: any) =>
            this._listItemEligibleForScreenPower(item),
          );
        },
      },
      {
        separator: true,
      },
      {
        name: 'Enable Display Control',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmEnableDisplayControl(items),
            selected,
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.displayControlService.enableDisplayControl.bind(
              this.displayControlService,
            ),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Disable Display Control',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmDisableDisplayControl(items),
            selected,
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.displayControlService.disableDisplayControl.bind(
              this.displayControlService,
            ),
            item.item,
          );
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        separator: true,
      },
      {
        name: 'Move To Company',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmMove(items),
            selected,
          );
        },
        actionCall: async (selected, companyId) => {
          if (selected.item.companyId === companyId) {
            return Promise.resolve();
          }
          return this.folderOperationService.executeSingleOperation(
            (item) => this.displayApiService.move(item.id, companyId),
            selected.item,
          );
        },
        reloadList: true,
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
      },
      {
        name: 'Export All',
        beforeBatchAction: (selected) =>
          this._confirmExport(selected.map((item) => item.item)),
        actionCall: this.displayApiService.export.bind(this.displayApiService),
        groupBy: true, //group all
      },
      {
        name: 'Share with others',
        beforeBatchAction: (selected) => {
          const accessControlList =
            selected.length === 1
              ? this.displayApiService.getAccessControlList(selected[0].item.id)
              : Promise.resolve({});

          return this.modalService.showMediumDialog(
            ShareWithUsersModalComponent,
            {
              accessControlList,
              accessLevels: ['Screen Sharing Moderator', 'Full Access'],
              defaultAccessLevel: 'Screen Sharing Moderator',
              saveAccessControlList: async (
                updatedAccessControlList: AccessControlList,
                comment?: string,
              ) => {
                await Promise.all(
                  selected.map(async (listItem) => {
                    const currentAccessControlList =
                      await this.displayApiService.getAccessControlList(
                        listItem.item.id,
                      );
                    await this.displayApiService.updateAccessControlList(
                      listItem.item.id,
                      {
                        ...currentAccessControlList,
                        ...(updatedAccessControlList as NonNullable<
                          Display['accessControlList']
                        >),
                      },
                      comment,
                    );
                  }),
                );
              },
            },
            { width: '640px', maxWidth: 'calc(100vw - 32px)' },
          );
        },
        actionCall: async () => {},
        hidden: (selected) => selected?.some((item) => item.isFolder),
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        requireRole: this.permissionService.isSaProductionReleaseEnabled()
          ? undefined
          : 'sa',
        showIn: ['single', 'bulk'],
        icon: 'add-user',
      },
      {
        name: 'Rename',
        beforeBatchAction: (selected) =>
          this.actionRename(selected.map((item) => item.item)),
        showIn: ['single'],
        icon: 'pencil-line',
      },
      {
        name: 'Move',
        beforeBatchAction: (selected) =>
          this.beforeActionMoveFolder(
            selected.map((item) => item.item),
            this.currentFolderId,
          ),
        actionCall: (selected, folderId) =>
          this.actionMoveFolder(selected.item, folderId),
        hidden: () =>
          !this.permissionService.hasAccess(DISPLAY_ACCESS.FULL_ACCESS),
        showIn: ['single', 'bulk'],
        icon: 'move-to-folder',
        mainOperation: true,
        reloadList: true,
      },
      {
        name: 'Delete',
        actionCall: async (listItem) => {
          await this.folderOperationService.executeSingleOperation(
            this.displayService.deleteDisplayByObject.bind(this.displayService),
            listItem.item,
          );
          if (listItem.isFolder) {
            await this.foldersApiService.delete(listItem.item.id);
          }
          return Promise.resolve();
        },
        requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        showIn: ['bulk', 'single'],
        icon: 'trash',
        isDelete: true,
        mainOperation: true,
      },
    ],
  } as ListOperations<Display>;
  folderOperations = this.listOperations.operations.filter(
    (operation) => operation.name !== 'Share with others',
  );

  private folderOperationService = inject(DISPLAY_FOLDER_OPERATION_SERVICE);

  currentFolderId: string = FoldersApiService.ROOT_FOLDER_ID;

  private _licenseError = false;

  constructor(
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private playerActionsService: PlayerActionsService,
    private playerLicenseService: PlayerLicenseService,
    private currentPlanService: CurrentPlanService,
    private displayService: DisplayService,
    private displayApiService: DisplayApiService,
    private scheduleService: ScheduleService,
    private displayControlService: DisplayControlService,
    private featuresService: FeaturesService,
    private modalService: ModalService,
    private foldersApiService: FoldersApiService,
    private playerProService: PlayerProService,
    private firmwareUpdateService: FirmwareUpdateService,
  ) {}

  private _confirmLicense(selected) {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    this._licenseError = false;
    if (!selected.length) {
      this.modalService.showMessage(
        'Already licensed!',
        'Please select some unlicensed displays to license.',
      );
      return Promise.reject();
    } else if (
      this._hasSubcompanyDisplays(selected) &&
      !company.shareCompanyPlan
    ) {
      this.modalService.showMessage(
        'Unable to share licenses!',
        'Some of the selected displays are for sub-companies, and your current plan does not allow sharing licenses. ' +
          'Please select displays from your company only.',
      );
      return Promise.reject();
    } else {
      return this.modalService.confirm(
        'Assign license?',
        'Do you want to assign licenses to the selected displays?',
        'Yes',
        'No',
      );
    }
  }

  private _showLicenseErrorIfNeeded(): Promise<void> {
    if (this._licenseError) {
      this._licenseError = false;
      this.modalService.showMessage(
        'Error',
        'An error occurred and all changes may not have been applied.',
      );
    }
    return Promise.resolve();
  }

  private _licenseDisplays(companyId, displays) {
    const displayIds = displays.map((display) => display.id);

    return this.playerLicenseService
      .licenseDisplaysByCompanyId(companyId, displayIds)
      .then(() => {
        displays.forEach((display) => {
          display.playerProAuthorized = true;
        });
      });
  }

  /**
   * List bulk screen power: online display with id. Uses isScreenPowerOnCompatiblePlayer
   * when the list row has enough fields; otherwise allows rows with playerVersion or
   * viewerVersion so sparse list payloads still match displays that work from detail.
   */
  private _listItemEligibleForScreenPower(
    listItem: ListItem<Display>,
  ): boolean {
    if (listItem.isFolder) {
      return false;
    }
    const display = listItem.item as Display;
    if (!display?.id) {
      return false;
    }
    const online =
      display.onlineStatus != null &&
      String(display.onlineStatus).toLowerCase() === 'online';
    if (!online) {
      return false;
    }
    const hasVersion = !!(display.playerVersion || display.viewerVersion);
    if (this.playerProService.isScreenPowerOnCompatiblePlayer(display)) {
      return true;
    }
    return hasVersion || !!(display.playerName && display.playerProAuthorized);
  }

  /**
   * List bulk reboot: uses isRebootCompatiblePlayer when the row has enough
   * fields to classify the player; otherwise allows sparse list rows (version or
   * licensed player name) so bulk actions align with detail when the API omits
   * playerName on list payloads.
   */
  private _listItemEligibleForReboot(listItem: ListItem<Display>): boolean {
    if (listItem.isFolder) {
      return false;
    }
    const display = listItem.item as Display;
    if (!display?.id) {
      return false;
    }
    if (display.playerName === 'RisePlayerPackagedApp') {
      return false;
    }
    if (this.playerProService.isRebootCompatiblePlayer(display)) {
      return true;
    }
    const hasVersion = !!(display.playerVersion || display.viewerVersion);
    return hasVersion || !!(display.playerName && display.playerProAuthorized);
  }

  private _checkLicenses(selectedItems) {
    const notAuthorized = [];
    selectedItems.forEach((display) => {
      if (!display.playerProAuthorized) {
        notAuthorized.push(display.id);
      }
    });
    if (notAuthorized.length > 0) {
      const availableLicenses =
        this.playerLicenseService.getProAvailableLicenseCount();
      const availableLicensesText = this.currentPlanService.isUnlimitedPlan()
        ? 'unlimited'
        : availableLicenses.toString();

      const company = this.companyStateService.getCopyOfSelectedCompany(true);
      if (
        this._hasSubcompanyDisplays(selectedItems) &&
        !company.shareCompanyPlan
      ) {
        this.modalService.showMessage(
          'Unable to complete bulk action!',
          'This action cannot be performed on unlicensed displays, and your current plan does not allow sharing licenses. ' +
            'Please select displays from your company only.',
        );
        return Promise.reject();
      }

      let baseMessage =
        notAuthorized.length > 1
          ? notAuthorized.length +
            ' of your selected displays are not licensed and to perform this action they need to be.'
          : '1 of your selected displays is not licensed and to perform this action it needs to be.';
      baseMessage +=
        ' You have ' +
        availableLicensesText +
        ' available license' +
        (availableLicenses !== 1 ? 's' : '');

      if (availableLicenses >= notAuthorized.length) {
        return this.modalService
          .confirm(
            'Almost there!',
            baseMessage +
              '. Do you want to assign ' +
              notAuthorized.length +
              (notAuthorized.length > 1
                ? ' to these displays?'
                : ' to this display?'),
            'Yes',
            'No',
          )
          .then(() => {
            const licenseOperation = this.listOperations.operations.find(
              (o) => {
                return o.name === 'License';
              },
            );
            return (licenseOperation as any).onClick(true);
          });
      } else {
        return this.modalService
          .confirm(
            'Almost there!',
            baseMessage +
              ' and you need to subscribe for ' +
              (notAuthorized.length - availableLicenses) +
              ' more to license ' +
              (notAuthorized.length > 1 ? 'these displays.' : 'this display.'),
            'Subscribe',
            'Cancel',
          )
          .then(() => {
            this.currentPlanService.showPurchaseOptions(
              notAuthorized.length - availableLicenses,
            );
            return Promise.reject();
          });
      }
    }
    return Promise.resolve();
  }

  private _confirmAssignSchedule(selectedItems) {
    if (this._hasSubcompanyDisplays(selectedItems)) {
      this.modalService.showMessage(
        'Schedule could not be assigned!',
        'Your schedule cannot be assigned to displays that belong to your sub-companies. <br/>Please select displays from your company only.',
      );
      return Promise.reject();
    }
    return this.modalService.showDialog(SchedulePickerModalComponent, null, {
      width: this.modalService.MODAL_SM,
    });
  }

  private _hasSubcompanyDisplays(selectedItems) {
    const cid = this.companyStateService.getSelectedCompanyId();
    return selectedItems.find((d) => {
      return d.companyId !== cid;
    });
  }

  private _confirmFirmwareAction(selectedItems) {
    const isMultiple = selectedItems.length > 1;
    const displayName = !isMultiple ? selectedItems[0].name : undefined;

    return this._checkLicenses(selectedItems).then(() => {
      // Show firmware update modal to get firmware URL
      return this.modalService
        .showFirmwareUpdateModal({
          displayName,
          isMultiple,
        })
        .then((result) => {
          if (!result || !result.firmwareUrl) {
            return Promise.reject('cancelled');
          }

          // For batch updates, use the firmware update service
          if (isMultiple) {
            const displays = selectedItems.map((item) => ({
              id: item.id,
              name: item.name,
            }));

            // Use the firmware update service for batch updates
            // This will handle Primus connections and notifications
            return this.firmwareUpdateService
              .requestBatchFirmwareUpdate(displays, result.firmwareUrl)
              .then(() => {
                // Return null to skip the individual actionCall iterations
                // since we handled everything in batch
                return Promise.reject('batch-handled');
              });
          }

          // For single updates, return the firmwareUrl to be passed to actionCall
          return Promise.resolve({ firmwareUrl: result.firmwareUrl });
        });
    });
  }

  private _confirmUpdateFirmware(selectedItems) {
    return this._confirmFirmwareAction(selectedItems);
  }

  private _confirmScreenSharing(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Screen Sharing',
        {
          screenSharingEnabled: true,
          screenSharingStandard: true,
          settings: {
            screenSharingMode: 'standard',
            screenSharingPinRequired: false,
            screenSharingAuthenticationRequired: false,
            screenSharingCodeHidden: false,
          },
        },
        EditScreenSharingComponent,
      );
    });
  }

  private _confirmSetMonitoring(selectedItems) {
    if (this.featuresService.isFeatureAvailable('monitoring')) {
      return this._checkLicenses(selectedItems).then(() => {
        return this.modalService.showBulkEditModal(
          'Set Monitoring',
          {
            monitoringEnabled: true,
            monitoringEmails: null,
            monitoringSchedule: null,
          },
          EditMonitoringComponent,
        );
      });
    }
    this.featuresService.showUpgradePlanModal('monitoring');
    return Promise.reject();
  }

  private _confirmSetRebootTime(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Reboot Time',
        {
          restartEnabled: true,
          restartTime: '02:00',
        },
        EditRebootTimeComponent,
      );
    });
  }

  private _confirmSetAddress(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Address',
        {
          useCompanyAddress: false,
          addressDescription: '',
          street: '',
          unit: '',
          city: '',
          country: '',
          province: '',
          postalCode: '',
          timeZoneOffset: null,
        },
        EditAddressComponent,
      );
    });
  }

  private _confirmControlAlertVolume(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showMediumDialog(EditAlertVolumeComponent, {
        settings: {
          volumeControlSupported: true,
          volumePercentage: 75,
        },
      });
    });
  }

  private _confirmLockKeyOperation(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Lock Buttons',
        {
          settings: {
            keyOperationModeDevice: 'normal',
          },
        },
        EditKeyOperationModeComponent,
      );
    });
  }

  private _confirmEnableDisplayControl(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      if (this.featuresService.isFeatureAvailable('display_control')) {
        return this.modalService.confirm(
          'Enable Display Control?',
          'Do you want to enable display control for the selected displays?',
          'Yes',
          'No',
        );
      }
      this.featuresService.showUpgradePlanModal('display_control');
      return Promise.reject();
    });
  }

  private _confirmDisableDisplayControl(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      if (this.featuresService.isFeatureAvailable('display_control')) {
        return this.modalService.confirm(
          'Disable Display Control?',
          'Do you want to disable display control for the selected displays?',
          'Yes',
          'No',
        );
      }
      this.featuresService.showUpgradePlanModal('display_control');
      return Promise.reject();
    });
  }

  private _confirmExport(selectedItems) {
    if (
      !this.currentPlanService.isPlanActive() &&
      !this.userStateService.isRiseAdmin()
    ) {
      this.currentPlanService.showUnlockThisFeatureModal();
      return Promise.reject();
    }
    return this.modalService.confirm(
      'Export displays?',
      'An export file will be prepared and emailed to you at <b>' +
        this.userStateService.getUserEmail() +
        '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
      'Export',
      'Cancel',
    );
  }

  private _confirmMove(selectedItems) {
    const plural = selectedItems.length > 1 ? 's' : '';
    return this.modalService.showDialog(CompanyIdModalComponent, {
      title: 'Move Selected Display' + plural,
      description:
        'The selected display' +
        plural +
        ' will be moved to the company ID specified below.',
    });
  }

  private async beforeActionMoveFolder(
    selectedItems: any,
    currentFolderId: string,
  ) {
    const folders = selectedItems.filter((item) => item.isFolder);
    return this.modalService
      .showDialog(
        MoveFolderDialogComponent,
        {
          kind: 'Display',
          parentId: currentFolderId,
          folderId: selectedItems[0].id,
          folders: folders.map((item) => item.id),
        },
        { width: this.modalService.MODAL_MD },
      )
      .then(async (parentId: string) => {
        if (parentId === undefined || parentId === null)
          return Promise.reject();
        return Promise.resolve(parentId);
      });
  }

  private async actionMoveFolder(selectedItem: any, parentId: string) {
    if (selectedItem.kind) {
      await this.foldersApiService.update(selectedItem.id, {
        ...selectedItem,
        parentId,
      });
    } else {
      await this.displayApiService.update(selectedItem.id, {
        folderId: parentId,
      });
    }
  }

  private actionRename(selectedItems: any) {
    const [item] = selectedItems;
    return this.modalService
      .showDialog(RenameDialogComponent, {
        title: 'Rename',
        placeholder: 'Enter display name',
        value: selectedItems[0].name,
        notAllowedNames: [],
      })
      .then(async (name: string | null) => {
        if (!name) return Promise.reject();
        if (item.isFolder) {
          try {
            await this.foldersApiService.update(selectedItems[0].id, {
              ...item,
              name,
              parentId: item.parentId ? item.parentId : undefined,
            });
          } catch (err) {
            this.modalService.showMessage('Error', err.message);
            return Promise.reject();
          }
        } else {
          await this.displayApiService.update(selectedItems[0].id, {
            name,
          });
        }
        item.name = name;
      });
  }
}
